import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Card, Label } from '@/entities/board';
import { contrastText } from '@/shared/lib/color';
import { formatCalendarDate } from '@/shared/lib/datetime';
import { Icons } from '@/shared/ui';

import { itemFill, labelColorsOf } from './labels';
import type { CalendarEvent, TaskGroups, UndatedTask } from './model';
import styles from './TaskPanel.module.css';

interface Props {
  groups: TaskGroups;
  /** Labels of the boards in scope (their colors paint the items). */
  labels: readonly Label[];
  colorOf: (boardId: string) => string;
  boardNameOf: (boardId: string) => string;
  /** Whether items can be dragged onto the grid to set / change their due date and time. */
  draggable: boolean;
  /** Start collapsed (tablet widths, where the panel sits under the grid). */
  collapsible: boolean;
  locale: string;
  onOpenCard: (cardId: string) => void;
}

/**
 * The right-hand task panel: tasks (cards with only a deadline, or without any date), never
 * events. Dated tasks are grouped by their due date; tasks without a date are listed under "No
 * date" and keep it. Dragging an item onto the grid sets only its END (date, and the time when
 * dropped on an hour): a task never gets an invented start.
 */
export function TaskPanel({
  groups,
  labels,
  colorOf,
  boardNameOf,
  draggable,
  collapsible,
  locale,
  onOpenCard,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!collapsible);
  const datedCount = groups.dated.reduce((sum, group) => sum + group.items.length, 0);
  const count = datedCount + groups.undated.length;

  const renderItem = (item: CalendarEvent | UndatedTask) => {
    const card: Card = item.card;
    const boardId = 'boardId' in item ? item.boardId : card.placement.board_id;
    const dated = 'kind' in item;
    const done = card.completed_at !== null;
    const fill = itemFill(labelColorsOf(card, labels));
    // A dated task is a DUE marker (a flag, "Due", and the due time when it has one).
    const dueText = dated
      ? item.allDay
        ? t('calendar.due')
        : t('calendar.dueAt', { time: item.start.slice(11, 16) })
      : '';
    const meta = [
      done ? t('calendar.completed') : '',
      card.is_overdue ? t('calendar.overdue') : '',
      card.archived_at ? t('calendar.archivedTag') : '',
    ]
      .filter(Boolean)
      .join(' · ');
    // A single label fills the item; several make hard-edged diagonal segments. The text is
    // always on a backing (or on the solid fill with its own contrast color).
    const style: CSSProperties | undefined =
      fill.kind === 'solid'
        ? { background: fill.color, color: contrastText(fill.color ?? '#ffffff') }
        : fill.kind === 'segments'
          ? { backgroundImage: fill.gradient }
          : undefined;
    return (
      <li key={item.id}>
        <button
          type="button"
          className={`${styles.item} ${done ? styles.done : ''} ${card.is_overdue ? styles.overdue : ''}`}
          data-deadline-card={'occurrence' in item && item.occurrence ? undefined : ''}
          data-card-id={item.id}
          data-testid="task-item"
          data-fill={fill.kind}
          draggable={false}
          style={style}
          title={boardNameOf(boardId)}
          onClick={() => {
            onOpenCard(item.id);
          }}
        >
          <span className={styles.dot} style={{ background: colorOf(boardId) }} aria-hidden />
          <span className={`${styles.itemMain} ${fill.kind === 'segments' ? styles.backed : ''}`}>
            <span className={styles.itemTitle}>{card.title}</span>
            {(dueText || meta) && (
              <span className={styles.itemMeta}>
                {dueText && (
                  <span className={styles.due} data-testid="panel-due">
                    <Icons.flag />
                    {dueText}
                  </span>
                )}
                {dueText && meta ? ' · ' : ''}
                {meta}
              </span>
            )}
          </span>
          {draggable && (
            <span className={styles.grip} aria-hidden>
              ⠿
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <aside
      className={`${styles.panel} ${open ? '' : styles.collapsed}`}
      aria-label={t('calendar.tasks')}
      data-testid="task-panel"
    >
      <h2 className={styles.title}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          disabled={!collapsible}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          {t('calendar.tasks')}
          <span className={styles.count}>{count}</span>
        </button>
      </h2>
      {open && (
        <div className={styles.body}>
          {draggable && count > 0 && <p className={styles.hint}>{t('calendar.tasksHint')}</p>}
          {groups.dated.length === 0 ? (
            <p className={styles.empty}>{t('calendar.noTasksInPeriod')}</p>
          ) : (
            groups.dated.map((group) => (
              <section
                key={group.date}
                aria-label={formatCalendarDate(group.date, locale)}
                data-testid="task-group"
                data-date={group.date}
              >
                <h3 className={styles.section}>{formatCalendarDate(group.date, locale)}</h3>
                <ul className={styles.list}>{group.items.map(renderItem)}</ul>
              </section>
            ))
          )}
          <section aria-labelledby="task-undated" data-testid="task-undated">
            <h3 id="task-undated" className={styles.section}>
              {t('calendar.noDate')}
              <span className={styles.sectionCount}>{groups.undated.length}</span>
            </h3>
            {groups.undated.length > 0 ? (
              <ul className={styles.list}>{groups.undated.map(renderItem)}</ul>
            ) : (
              <p className={styles.empty}>{t('calendar.noUndated')}</p>
            )}
          </section>
        </div>
      )}
    </aside>
  );
}
